<template>
    <div>
        <canvas ref="canvas" :height="height"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const compacto = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

export default {
    name: 'BarChart',

    props: {
        labels: { type: Array, default: () => [] },
        datasets: { type: Array, default: () => [] },
        height: { type: Number, default: 260 },
        currency: { type: Boolean, default: true },
        horizontal: { type: Boolean, default: false },
        stacked: { type: Boolean, default: false },
    },

    data: () => ({
        chart: null,
    }),

    mounted() {
        this.render();
    },

    beforeDestroy() {
        if (this.chart) this.chart.destroy();
    },

    watch: {
        labels() { this.render(); },
        datasets: { handler() { this.render(); }, deep: true },
    },

    methods: {
        render() {
            if (this.chart) this.chart.destroy();
            const ctx = this.$refs.canvas;
            if (!ctx) return;

            // Eixo em valor cheio fica ilegível: no eixo abrevia (R$ 25 mil),
            // no tooltip mostra o valor exato em pt-BR.
            const fmtCurrency = v => brl.format(Number(v) || 0);
            const fmtAxis = v => {
                const n = Number(v) || 0;
                if (n === 0) return 'R$ 0';
                if (Math.abs(n) >= 1000000) return 'R$ ' + compacto.format(n / 1000000) + ' mi';
                if (Math.abs(n) >= 1000) return 'R$ ' + compacto.format(n / 1000) + ' mil';
                return brl.format(n);
            };

            const styledDatasets = this.datasets.map((ds, i) => ({
                label: ds.label,
                data: ds.data,
                backgroundColor: ds.color || this.defaultColor(i, 0.7),
                borderColor: ds.color || this.defaultColor(i, 1),
                borderWidth: 1,
            }));

            this.chart = new Chart(ctx, {
                type: this.horizontal ? 'horizontalBar' : 'bar',
                data: {
                    labels: this.labels,
                    datasets: styledDatasets,
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { position: 'top' },
                    scales: {
                        xAxes: [{
                            stacked: this.stacked,
                            ticks: this.horizontal && this.currency
                                ? { beginAtZero: true, callback: fmtAxis }
                                : { beginAtZero: true },
                        }],
                        yAxes: [{
                            stacked: this.stacked,
                            ticks: !this.horizontal && this.currency
                                ? { beginAtZero: true, callback: fmtAxis }
                                : { beginAtZero: true },
                        }],
                    },
                    tooltips: this.currency ? {
                        callbacks: {
                            label: (item, chartData) => {
                                const ds = chartData.datasets[item.datasetIndex];
                                const val = this.horizontal ? item.xLabel : item.yLabel;
                                return `${ds.label}: ${fmtCurrency(val)}`;
                            },
                        },
                    } : {},
                },
            });
        },

        defaultColor(i, alpha) {
            const palette = [
                `rgba(25, 118, 210, ${alpha})`,
                `rgba(76, 175, 80, ${alpha})`,
                `rgba(255, 152, 0, ${alpha})`,
                `rgba(156, 39, 176, ${alpha})`,
                `rgba(244, 67, 54, ${alpha})`,
            ];
            return palette[i % palette.length];
        },
    },
};
</script>
